import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import ListPageHeader from "@/components/ListPageHeader/ListPageHeader.vue";

import { inventoryLotsController } from "@/controllers/inventoryLots";
import { InventoryLot } from "@/models/InventoryLot";

import { JSConn } from "@/utilities/js-conn";
import { itemRouteBuilder, NamedRts } from "@/router";

import { genericSort, sortIcon } from "@/components/helpers";

import BarPercentageChart from "@/components/BarPercentageChart/BarPercentageChart.vue";

import { debounce } from "@/utilities/debounce";

import {
  arrowUpHandler,
  arrowDownHandler,
  filterOnInputHandler,
  filterItems,
} from "@/utilities/pickListHelpers";

export default defineComponent({
  name: "inventoryLots",
  props: {
    useMode: {
      type: String,
      required: false,
    },
  },
  components: {
    LoadingAnimation,
    BarPercentageChart,
    ListPageHeader,
  },

  setup(props, ctx) {
    const $store = ctx.root.$store;

    const dataLoaded = ref(false);
    const loadingMessage = ref(
      "Please wait while the inventory lots are loaded...",
    );

    const firstInput = ref<null | HTMLInputElement>(null);

    const showMenu = ref(false);

    const toggleMenu = (newState: boolean) => {
      showMenu.value = newState;
      if (!newState) {
        ctx.root.$nextTick(() => {
          if (firstInput.value) {
            firstInput.value.focus();
          }
        });
      }
    };

    let currentStatuses = ["available"];
    const filterableKeys: Array<keyof InventoryLot> = [
      "label",
      "client_label",
      "commodity_label",
    ];

    const filter = ref("");

    const clearFilter = () => {
      filterOnInputHandler(
        apiInventoryLots,
        inventoryLots,

        "",
        filter,
        debouncedFilteredInventoryLots,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterInventoryLots = (filter: string, il: InventoryLot[]) => {
      return filterItems(filter, filterableKeys, il);
    };

    const debouncedFilteredInventoryLots = debounce(filterInventoryLots, 500);

    const sortKey = ref<keyof InventoryLot>("commodity_label");
    const sortAsc = ref(true);

    const apiInventoryLots = computed(() => {
      return $store.getters.inventoryLots as InventoryLot[];
    });

    const showRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.inventoryLotDetails, id);
    };

    const editRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.inventoryLotEditForm, id);
    };

    const inventoryLots = ref<InventoryLot[]>([]);

    const localSortIcon = (lSortKey: keyof InventoryLot) => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const getInventoryLotsByStatus = (statuses: string[]) => {
      currentStatuses = [...statuses];
      dataLoaded.value = false;
      const jsConn = new JSConn();
      jsConn.formParams = {
        statuses: statuses,
      };
      return inventoryLotsController.needInventoryLots(jsConn).then(() => {
        inventoryLots.value = [...apiInventoryLots.value];
        // flip the sort, otherwise on load it would be off
        sortAsc.value = !sortAsc.value;
        updateSort(sortKey.value);
        dataLoaded.value = true;
        return Promise.resolve();
      });
    };

    const updateSort = (lSortKey: keyof InventoryLot) => {
      inventoryLots.value = genericSort(
        [...inventoryLots.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    onMounted(() => {
      getInventoryLotsByStatus(currentStatuses).finally(() => {
        dataLoaded.value = true;

        if (props.useMode !== "pickerMode") {
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        }
      });
    });

    // this is for when the inventory lots are used as a picker
    // they make the arrow keys work correctly
    const selectedIndex = ref(-1);

    const onArrowDown = (evt: any) => {
      evt.preventDefault();
      arrowDownHandler(selectedIndex, inventoryLots.value.length);
    };

    const onArrowUp = (evt: any) => {
      evt.preventDefault();
      arrowUpHandler(selectedIndex);
    };

    const filterOnInput = ({ target }: { target: HTMLInputElement }) => {
      filterOnInputHandler(
        apiInventoryLots,
        inventoryLots,

        target.value,
        filter,
        debouncedFilteredInventoryLots,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterOnEnter = (evt: any) => {
      evt.preventDefault();
      let tmpResult = inventoryLots.value[selectedIndex.value];

      if (!tmpResult) {
        tmpResult = inventoryLots.value[0];
      }

      if (props.useMode === "pickerMode") {
        sendInventoryLot(tmpResult);
      } else {
        ctx.root.$router.push(showRouteBuilder(tmpResult.id));
      }
    };

    const sendInventoryLot = (il: InventoryLot) => {
      ctx.emit("sendInventoryLot", il);
    };

    return {
      dataLoaded,
      loadingMessage,
      inventoryLots,

      filter,
      filterOnEnter,
      filterOnInput,
      clearFilter,

      getInventoryLotsByStatus,
      updateSort,
      localSortIcon,

      showRouteBuilder,
      editRouteBuilder,

      onArrowDown,
      onArrowUp,
      selectedIndex,
      sendInventoryLot,

      showMenu,
      firstInput,
      toggleMenu,
    };
  },
});
